import { useReporting } from '@/hooks/useReporting';
import { getCurrentPageViewDepth } from '@/hooks/usePageViewDepth';
import useRouteInfo from '@/hooks/useRouteInfo';
import {
  getSiteRedirectEnterPageScene,
  getSiteRedirectReferrerInfo,
} from '@/hooks/useSiteRedirect';
import { ProviderType } from '@/reporting/types';
import logger from '@/utils/client-logger';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import { isInZeloolApp, setSession } from '@/utils/tools';
import { useThrottleFn } from 'ahooks';
import { useCallback, useEffect, useRef } from 'react';
import type { HomeButtonTrackingParams } from '../types';

export const handlePageFlagsInfo = (params: any) => {
  const getValue = (value: any) => {
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return `${value}`;
    if (!value) return '-';
    return `${value}`;
  };

  const rule1 = params.mkt_jump_type === '1' && params.group_id;
  if (rule1) {
    try {
      const aCode = 'home';
      const bCode = getValue(params.module_id);
      const cCode = getValue(params.module_title);
      const dCode = getValue(params.module_index);
      const eCode = getValue(params.module_name);
      const fCode = getValue(params.module_tab_name);
      const gCode = getValue(params.mkt_index);
      const hCode = getValue(params.mkt_index_sub);
      const iCode = getValue(params.group_id);
      const arr = [aCode, bCode, cCode, dCode, eCode, fCode, gCode, hCode, iCode];
      const str = arr.join('@');
      return str.replaceAll('-', '');
    } catch (error) {
      logger.error('handlePageFlagsInfo error:', error);
    }
  }
};

/**
 * 首页神策埋点Hook
 * 对应Vue版本中index.vue的神策埋点功能
 */
export function useHomeSensorsTracking() {
  const { getFromRouterInfo } = useRouteInfo();
  const { trackEvent } = useReporting();

  // 页面浏览状态跟踪
  const homeViewSended = useRef(false);
  const enterTimer = useRef(Date.now());
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });

  /**
   * 神策埋点：首页资源位曝光
   * 对应Vue版本中的sdHomeMktExp函数
   * @TrackType sensorsdata
   * @EventType HomeMkt_Exp
   * @EventDesc 首页资源位曝光
   * @EventParams {
   *   module_id: 模块ID,
   *   module_index: 模块排序,
   *   module_name: 模块名称,
   *   mkt_index: 模块下内容排序,
   *   mkt_index_sub: 子模块下内容排序,
   *   group_id: 分组id,
   *   group_name: 分组名称,
   *   shop_sku: 站点销售sku,
   *   business_sku: 站点平台sku,
   *   product_sku: 商品sku,
   *   product_id: 商品ID,
   *   product_name: 商品名称,
   * }
   */
  const trackHomeMktExp = useCallback(
    (params: any) => {
      /**
       * 如果是内嵌app，跳过上报。内嵌页不包括首页。
       * 这边需要防止其他错误页面导致重定向回首页引发的错误上报。
       * App内部直接引用的前端页面范围如下：
       * https://alidocs.dingtalk.com/i/nodes/m9bN7RYPWdlKmdBbIbanEA9pWZd1wyK0?cid=72752520019&utm_source=im&utm_scene=team_space&iframeQuery=utm_medium%3Dim_card%26utm_source%3Dim&utm_medium=im_card&corpId=ding84ad0c7e075023e935c2f4657eb6378f
       */
      if (isInZeloolApp()) {
        return;
      }
      try {
        trackEvent(
          {
            eventName: 'HomeMkt_Exp',
            properties: {
              ...params,
              view_advance: getCurrentPageViewDepth(),
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('HomeMkt_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  const trackHomeMktValidExp = useCallback(
    (params: any) => {
      try {
        trackEvent(
          {
            eventName: 'HomeValidMkt_Exp',
            properties: {
              ...params,
              view_advance: getCurrentPageViewDepth(),
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('HomeMkt_Exp tracking error:', error);
      }
    },
    [trackEvent],
  );

  /**
   * 神策埋点：首页资源位点击
   * 对应Vue版本中的SDHomeMktClick函数
   * @TrackType sensorsdata
   * @EventType HomeMkt_Click
   * @EventDesc 首页资源位点击
   * @EventParams {
   *   module_id: 模块ID,
   *   module_index: 模块排序,
   *   module_name: 模块名称,
   *   mkt_index: 模块下内容排序,
   *   mkt_index_sub: 子模块下内容排序,
   *   mkt_jump_type: 模块下内容的跳转类型,
   *   mkt_jump_content: 模块下跳转的内容,
   *   module_tab_name: 模块内tab名称,
   *   coupon_id_list: 优惠券ID,
   *   coupon_name_list: 优惠券名称,
   *   group_id: 分组id,
   *   group_name: 分组名称,
   *   shop_sku: 站点销售sku,
   *   business_sku: 站点平台sku,
   *   product_sku: 商品sku,
   *   product_id: 商品ID,
   *   product_name: 商品名称,
   * }
   */
  const trackHomeMktClick = useCallback(
    (params: any) => {
      try {
        trackEvent(
          {
            eventName: 'HomeMkt_Click',
            properties: {
              ...params,
            },
          },
          [ProviderType.SENSORS],
        );
        const pageFlagsInfo = handlePageFlagsInfo(params);
        setSession('zl_home_page_flags_info', pageFlagsInfo);
      } catch (error) {
        logger.error('HomeMkt_Click tracking error:', error);
      }
    },
    [trackEvent],
  );

  const trackHomeBtnClick = (params: HomeButtonTrackingParams) => {
    try {
      trackEvent(
        {
          eventName: 'HomeBtn_Click',
          properties: params,
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('HomeBtn_Click tracking error:', error);
    }
  };

  /**
   * 神策埋点：首页页面浏览
   * 对应Vue版本中的SDHomeView函数
   * @TrackType sensorsdata
   * @EventType Home_View
   * @EventDesc 首页页面浏览
   * @EventParams {
   *   refer_page_name: 来源页面名称,
   *   refer_url_path: 来源页面路径,
   * }
   */
  const trackHomeView = useCallback(() => {
    try {
      if (homeViewSended.current) return;
      homeViewSended.current = true;

      // 重置开始时间
      enterTimer.current = Date.now();
      const { refer_page_name, refer_url_path } = getFromRouterInfo();
      const referrerInfo = getSiteRedirectReferrerInfo();
      trackEvent(
        {
          eventName: 'Home_View',
          properties: {
            refer_page_name: referrerInfo.referrer_page_name || refer_page_name,
            refer_url_path: referrerInfo.referrer_url_path || refer_url_path,
            enter_page_scene: getSiteRedirectEnterPageScene(referrerInfo),
            from_site_info: referrerInfo.site_jump_from,
          },
        },
        [ProviderType.SENSORS],
      );
    } catch (error) {
      logger.error('Home_View tracking error:', error);
    }
  }, [trackEvent]);

  /**
   * 神策埋点：首页页面离开
   * 对应Vue版本中的SDHomeLeave函数
   * @TrackType sensorsdata
   * @EventType Home_Leave
   * @EventDesc 首页页面离开
   * @EventParams {
   *   refer_page_name: 来源页面名称,
   *   refer_url_path: 来源页面路径,
   *   view_advance: 浏览进度,
   *   view_duration: 浏览时长,
   *   view_area: 浏览区域,
   * }
   */
  const trackHomeLeave = useCallback(
    (_fromRouterInfo: any) => {
      try {
        if (!homeViewSended.current) return;
        homeViewSended.current = false;

        // 计算浏览时长（秒）
        const viewDuration = Math.ceil((Date.now() - enterTimer.current) / 1000);

        // 使用保存的滚动状态，避免页面跳转时滚动位置被重置的问题
        const { viewAdvance, viewArea } = lastScrollState.current;

        const { refer_page_name, refer_url_path } = _fromRouterInfo;
        trackEvent(
          {
            eventName: 'Home_Leave',
            properties: {
              refer_page_name,
              refer_url_path,
              view_advance: viewAdvance,
              view_duration: viewDuration,
              view_area: viewArea,
            },
          },
          [ProviderType.SENSORS],
        );
      } catch (error) {
        logger.error('Home_Leave tracking error:', error);
      }
    },
    [trackEvent],
  );

  // 使用ahooks的useThrottleFn优化滚动事件性能
  const updateScrollState = useCallback(() => {
    try {
      const viewAdvance = getCurrentScrollPercentage();
      const viewArea = getViewArea();
      lastScrollState.current = { viewAdvance, viewArea };
    } catch (error) {
      // 忽略错误，使用默认值
      logger.error('Error updating scroll state:', error);
    }
  }, []);

  // 使用ahooks的节流Hook，100ms节流
  const { run: throttledUpdateScrollState } = useThrottleFn(updateScrollState, {
    wait: 100,
  });

  // 定期更新滚动状态，避免页面跳转时丢失滚动信息
  useEffect(() => {
    // 立即更新一次
    updateScrollState();

    // 监听滚动事件，使用ahooks节流处理
    const handleScroll = () => {
      throttledUpdateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [updateScrollState, throttledUpdateScrollState]);

  // 参考blog文章的导航拦截处理
  useEffect(() => {
    const _fromRouterInfo = getFromRouterInfo();
    return () => {
      trackHomeLeave(_fromRouterInfo);
    };
  }, []);

  return {
    // 页面级埋点
    trackHomeView,
    trackHomeLeave,
    // 模块级埋点
    trackHomeMktExp,
    trackHomeMktClick,
    trackHomeMktValidExp,
    trackHomeBtnClick,
  };
}
